import apiSlice from 'app/apiSlice';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { sortToString } from 'helpers/paramHelpers';

// Create an entity adapter for users
const usersAdapter = createEntityAdapter();

const usersApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getUsers: builder.query({
      query: params => ({
        url: `accounts`,
        params: {
          ...params,
          sort: sortToString(params.sort || []),
        },
      }),
      transformResponse: response => {
        const normalized = usersAdapter.setAll(
          usersAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
      providesTags: ['Users'], //tag for refetch when invalidateTags api trggered
    }),

    getUserById: builder.query({
      query: id => ({
        url: `accounts/${id}`,
      }),
      transformResponse: response => {
        return response.progpack_account;
      },
      providesTags: ['User'], //tag for refetch when invalidateTags api trggered
    }),

    getInvitations: builder.query({
      query: params => ({
        url: `invitations`,
        params: {
          ...params,
          sort: sortToString(params.sort || []),
        },
      }),
      transformResponse: response => {
        const normalized = usersAdapter.setAll(
          usersAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
    }),

    createUser: builder.mutation({
      query: data => ({
        url: 'accounts',
        method: 'POST',
        body: data,
      }),
      transformResponse: response => {
        return response.data;
      },
      invalidatesTags: ['Users'], //trigger refetch for the providesTags api
    }),

    updateUserById: builder.mutation({
      query: ({ id, data }) => ({
        url: `accounts/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Users', 'User'], //trigger refetch for the providesTags api
    }),

    deleteUserById: builder.mutation({
      query: id => ({
        url: `accounts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users', 'User'], //trigger refetch for the providesTags api
    }),

    createInvitation: builder.mutation({
      query: data => ({
        url: 'invitations',
        method: 'POST',
        body: data,
      }),
      transformResponse: response => {
        return response.data;
      },
    }),

    checkInvitationToken: builder.query({
      query: token => ({
        url: `invitations/check`,
        params: {
          token,
        },
      }),
      transformResponse: response => {
        return response.data;
      },
    }),
  }),
  overrideExisting: false, // Avoid overwriting existing endpoints
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetInvitationsQuery,
  useCreateUserMutation,
  useUpdateUserByIdMutation,
  useDeleteUserByIdMutation,
  useCreateInvitationMutation,
  useLazyCheckInvitationTokenQuery,
} = usersApi;

export default usersApi;
